import { useDispatch, useSelector } from "react-redux";
import { useLoginMutation, useLogOutMutation, useResendOTPMutation, useVerifyOTPMutation } from "../../api/auth/auth-api";
import { clearAllAuth, clearCredentials, clearLoginCredential, selectUserEmail, setCredentials, setLoginCredential, setPendingEmail } from "../../features/auth/auth-slice";
import { clearTypeModal, setTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useState } from "react";
import { getRememberedLogins, RememberedLogin, rememberLogin, removeRememberedLogin } from "../../utils/remembered-login.utils";
import { authSession } from "../../helper/auth/auth-session";
import { getDepartmentHome } from "../common/use-get-department-page";
import { clearSelectedPendingEmail } from "../../features/gms-state-and-modal/state-slice";
import { useNavigate } from "react-router";
import { useToastify } from "../../library/toastify/toast-provider";
import { useAuthDataState } from "../data-state/use-auth-data-state";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useAuthActions = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showToastNotify } = useToastify();
    const { isVerifyOTPModal: isVerify } = useModalAccess()
    const {
        pendingEmail,

        loginEmail,
        loginPassword,
        loginCredentials,
    } = useAuthDataState()

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [rememberedLogins, setRememberedLogins] = useState<RememberedLogin[]>([]);
    const [rememberMe, setRememberMe] = useState<boolean>(false);

    const email = loginEmail.trim() ?? '';
    const password = loginPassword.trim() ?? '';

    const isValidEmail = email.length > 3 && (email.endsWith('@gmail.com') || email.endsWith('.com.ph'));
    const isValidPassword = password.length > 3;
    const isBtnDisable = !isValidEmail || !isValidPassword;

    const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
    const userEmail = useSelector(selectUserEmail);

    const [login, { isLoading: isLoadingLogin }] = useLoginMutation();
    const [verifyOTP, { isLoading: isLoadingVerifyOTP }] = useVerifyOTPMutation();
    const [resendOTP, { isLoading: isLoadingResendOTP }] = useResendOTPMutation();
    const isLoadingVerifyResend = isLoadingVerifyOTP || isLoadingResendOTP;
    const [logOut, { isLoading: isLoadingLogOut }] = useLogOutMutation();
    
    const handleLogin = async () => {
        if (isBtnDisable) return
        try {
            const loginResponse = await login({ 
                email: loginEmail, 
                password: loginPassword, 
            }).unwrap();
            if (loginResponse.status) {
                dispatch(setPendingEmail(loginEmail));
                sessionStorage.setItem(
                    'rememberMePending',
                    JSON.stringify({
                        email: loginEmail,
                        password: loginPassword,
                        rememberMe,
                    })
                );

                dispatch(setTypeModal('verifyOTPModal'));
                dispatch(clearLoginCredential());
                return;
            };
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    const handleSelectRememberedAccount = ( account: RememberedLogin ) => {
        dispatch(setLoginCredential({
            ...loginCredentials,
            email: account.email,
            password: account.password,
        }));

        setRememberMe(true);
        setShowPassword(false);
    };

    const handleRemoveRememberedAccount = ( e: React.MouseEvent, email: string ) => {
        e.stopPropagation();
        removeRememberedLogin(email);
        const updatedAccounts = getRememberedLogins();
        setRememberedLogins(updatedAccounts);

        if ( loginEmail?.toLowerCase() === email.toLowerCase() ) {
            dispatch(clearLoginCredential());
            setRememberMe(false);
            setShowPassword(false);
        }
    };

    const handleVerifyOTP = async (verificationCode: string) => {
        try {
            if (!pendingEmail) {
                showToastNotify('Email is required', 'warning');
                return;
            }

            if (verificationCode.length !== 6) {
                showToastNotify('OTP must be 6 digits', 'warning');
                return;
            }

            const verifyOTPResponse = await verifyOTP({
                email: pendingEmail,
                otp: verificationCode,
            }).unwrap();

            const status = verifyOTPResponse.status;
            const accessToken = verifyOTPResponse.accessToken;
            const refreshToken = verifyOTPResponse.refreshToken;
            const user = verifyOTPResponse.user;

            const pendingRememberMe = sessionStorage.getItem('rememberMePending');

            if (pendingRememberMe) {
                try {
                    const {
                        email: rememberedEmail,
                        password: rememberedPassword,
                        rememberMe,
                    } = JSON.parse(pendingRememberMe);

                    if (rememberMe && rememberedEmail && rememberedPassword) {
                        rememberLogin(rememberedEmail, rememberedPassword);
                    }
                } catch (error) {
                    showToastNotify(`Failed to process Remember Me`, 'error');
                } finally {
                    sessionStorage.removeItem('rememberMePending');
                }
            }

            authSession.setSession();

            dispatch(setCredentials({
                status,
                accessToken,
                refreshToken,
                user,
            }));

            navigate(getDepartmentHome(user.departmentCode), { replace: true });
            dispatch(clearTypeModal());
            dispatch(clearSelectedPendingEmail());

            showToastNotify('OTP verified successfully', 'success');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };
    
    const handleResendOTP = async () => {
        if (!pendingEmail) {
            return;
        }

        try {
            await resendOTP({ email: pendingEmail }).unwrap();
            showToastNotify('OTP resent successfully', 'success');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    const handleCloseVerifyOTPModal = () => {
        dispatch(clearTypeModal());
        dispatch(clearSelectedPendingEmail());
        sessionStorage.removeItem('rememberMePending');
    };

    const handleLogout = async () => {
        if (!userEmail) {
            showToastNotify('Email is required.', 'warning');
            return;
        }

        try {
            await logOut({ email: userEmail }).unwrap();
            dispatch(clearCredentials());
            setShowLogoutModal(false);
            showToastNotify('Logout successfully.', 'success');
            dispatch(clearAllAuth());
            authSession.clearSession();
            navigate('/login');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    return {
        // login
        isLoadingLogin,
        setRememberedLogins,
        rememberedLogins,
        // loginCredential: loginCredentials,
        rememberMe,
        setRememberMe,
        showPassword,
        setShowPassword,
        isBtnDisable,
        isVerify,
        handleLogin,
        handleSelectRememberedAccount,
        handleRemoveRememberedAccount,

        // verify and resend OTP
        // code,
        // setCode,
        // inputRefs,
        handleVerifyOTP,
        handleResendOTP,
        handleCloseVerifyOTPModal,
        isLoadingVerifyResend,

        // logout
        isLoadingLogOut,
        handleLogout,
        showLogoutModal,
        setShowLogoutModal,
    }
}